import { useCallback, useEffect, useMemo, useState } from "react";
import {
  KEYBOARD_SHORTCUT_DEFINITIONS,
  KEYBOARD_SHORTCUTS_CHANGED_EVENT,
  bindingFingerprint,
  findShortcutBindingConflict,
  isForbiddenRemapBinding,
  normalizeBinding,
  persistKeyboardShortcutOverrides,
  readKeyboardShortcutOverrides,
  resolveKeyboardShortcutBindings,
} from "./keyboardShortcuts";

/**
 * Resolve a stable per-user storage key from auth user.
 *
 * @example
 * resolveKeyboardShortcutsUserKey({ email: "a@b.com", id: "1" });
 * // "1"
 */
export const resolveKeyboardShortcutsUserKey = (user) => {
  const id = String(user?.id || user?.user_id || "").trim();
  if (id) return id;
  const email = String(user?.email || "").trim().toLowerCase();
  if (email) return email;
  return "anonymous";
};

/**
 * Live effective keyboard bindings for the current user (localStorage).
 *
 * @example
 * const { bindings, setBinding, resetBinding, resetAll } = useKeyboardShortcutsPreference(user);
 * setBinding("task.add", { key: "c" });
 */
export const useKeyboardShortcutsPreference = (user) => {
  const userKey = resolveKeyboardShortcutsUserKey(user);
  const [overrides, setOverrides] = useState(() => readKeyboardShortcutOverrides(userKey));

  useEffect(() => {
    setOverrides(readKeyboardShortcutOverrides(userKey));
  }, [userKey]);

  useEffect(() => {
    const sync = (event) => {
      const detailKey = event?.detail?.userKey;
      if (detailKey && detailKey !== userKey) return;
      setOverrides(readKeyboardShortcutOverrides(userKey));
    };
    window.addEventListener(KEYBOARD_SHORTCUTS_CHANGED_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(KEYBOARD_SHORTCUTS_CHANGED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [userKey]);

  const bindings = useMemo(() => {
    /** @type {Record<string, import("./keyboardShortcuts").KeyBinding>} */
    const resolved = {};
    for (const definition of KEYBOARD_SHORTCUT_DEFINITIONS) {
      if (!definition.editable) {
        resolved[definition.id] = { ...definition.defaultBinding };
        continue;
      }
      const override = overrides[definition.id];
      resolved[definition.id] = override
        ? { ...override }
        : { ...definition.defaultBinding };
    }
    return resolved;
  }, [overrides]);

  const setBinding = useCallback(
    (shortcutId, nextBinding) => {
      const definition = KEYBOARD_SHORTCUT_DEFINITIONS.find((entry) => entry.id === shortcutId);
      if (!definition?.editable) {
        return { ok: false, error: "This shortcut cannot be changed." };
      }
      const normalized = normalizeBinding(nextBinding);
      if (!normalized || isForbiddenRemapBinding(normalized)) {
        return { ok: false, error: "That key cannot be used." };
      }
      const tentative = { ...bindings, [shortcutId]: normalized };
      const conflict = findShortcutBindingConflict(shortcutId, normalized, tentative);
      if (conflict) {
        return {
          ok: false,
          error: `Already used by “${conflict.label}”.`,
        };
      }
      const nextOverrides = { ...overrides };
      if (bindingFingerprint(normalized) === bindingFingerprint(definition.defaultBinding)) {
        delete nextOverrides[shortcutId];
      } else {
        nextOverrides[shortcutId] = normalized;
      }
      persistKeyboardShortcutOverrides(userKey, nextOverrides);
      setOverrides(nextOverrides);
      return { ok: true };
    },
    [bindings, overrides, userKey]
  );

  const resetBinding = useCallback(
    (shortcutId) => {
      const definition = KEYBOARD_SHORTCUT_DEFINITIONS.find((entry) => entry.id === shortcutId);
      if (!definition?.editable) return;
      const nextOverrides = { ...overrides };
      delete nextOverrides[shortcutId];
      persistKeyboardShortcutOverrides(userKey, nextOverrides);
      setOverrides(nextOverrides);
    },
    [overrides, userKey]
  );

  const resetAll = useCallback(() => {
    persistKeyboardShortcutOverrides(userKey, {});
    setOverrides({});
  }, [userKey]);

  return {
    userKey,
    bindings,
    overrides,
    setBinding,
    resetBinding,
    resetAll,
    definitions: KEYBOARD_SHORTCUT_DEFINITIONS,
  };
};

/**
 * Read-only effective bindings that stay in sync with preference changes.
 *
 * @example
 * const bindings = useResolvedKeyboardShortcutBindings(user);
 * bindings["search.focus"]; // { key: "/" }
 */
export const useResolvedKeyboardShortcutBindings = (user) => {
  const userKey = resolveKeyboardShortcutsUserKey(user);
  const [bindings, setBindings] = useState(() => resolveKeyboardShortcutBindings(userKey));

  useEffect(() => {
    setBindings(resolveKeyboardShortcutBindings(userKey));
  }, [userKey]);

  useEffect(() => {
    const sync = (event) => {
      const detailKey = event?.detail?.userKey;
      if (detailKey && detailKey !== userKey) return;
      setBindings(resolveKeyboardShortcutBindings(userKey));
    };
    window.addEventListener(KEYBOARD_SHORTCUTS_CHANGED_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(KEYBOARD_SHORTCUTS_CHANGED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [userKey]);

  return bindings;
};
